<script>
import { mapGetters } from 'vuex';
import icons from '@/schemas/icons';
import ImageLoader from '@/components/shared/ImageLoader.vue';

export default {
  name: 'AppTeaser',

  components: {
    ImageLoader,
  },

  props: {
    teaser: {
      type: Object,
      default: () => {},
    },

    size: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      icons,
    };
  },

  computed: {
    ...mapGetters('menu', ['getPageLink']),

    icon() {
      // eslint-disable-next-line
      return require(`@/assets/svg-icons/${icons[this.teaser.icon]}.svg`);
    },

    linkType() {
      return this.teaser.link.fields.url ? 'a' : 'router-link';
    },

    attribute() {
      return this.linkType === 'a' ? { href: this.link } : { to: this.link };
    },

    link() {
      return this.getPageLink(this.teaser.link);
    },
  },
};
</script>

<template>
  <component :is="linkType" class="teaser" v-bind="attribute" :class="size" v-if="teaser">
    <!-- Small teaser -->
    <template v-if="size === 'small'">
      <img v-if="teaser.icon" :src="icon" width="24" height="24" />
      <h2 v-if="teaser.heading">{{ teaser.heading }}</h2>
      <p>{{ teaser.longText }}</p>

      <div v-if="teaser.linkText" class="link-wrapper">
        <span class="link">{{ teaser.linkText }}</span>
      </div>
    </template>

    <!-- Medium teaser -->
    <template v-if="size === 'medium'">
      <h2 v-if="teaser.heading">{{ teaser.heading }}</h2>
      <h3 v-if="teaser.subHeading" class="sub-heading">{{ teaser.subHeading }}</h3>

      <ImageLoader v-if="teaser.image" class="image" :imgSrc="teaser.image.fields.file" />

      <p>{{ teaser.longText }}</p>
    </template>

    <!-- Large teaser -->
    <template v-if="size === 'large'">
      <div class="block">
        <header class="header">
          <h3>{{ teaser.heading }}</h3>
          <img class="icon" src="@/assets/svg-icons/arrow-right.svg" width="24" height="24" alt />
        </header>

        <h2>{{ teaser.subHeading }}</h2>
      </div>

      <div v-if="teaser.image" class="image block">
        <ImageLoader :imgSrc="teaser.image.fields.file" />
      </div>
    </template>
  </component>
</template>

<style lang="scss" scoped>
.teaser {
  @include teaser;

  p {
    margin-bottom: 0;
  }

  &.small {
    @include teaser-small;
    text-align: center;
    align-items: center;

    &:hover {
      .link {
        color: darken($interaction-color-primary, 10%);
      }
    }

    h2 {
      @include secondary-text-bold;
      margin: $space-16 0;
    }

    p {
      margin: 0;
      max-width: 100%;
    }
  }

  &.medium {
    h2 {
      margin: 0 0 $space-8;
    }

    .sub-heading {
      @include caption-medium;
      color: $text-color-secondary;
      margin: 0;

      @include tablet-large {
        margin: 0 0 (-$space-8);
      }
    }

    .image {
      margin-top: $space-16;
      width: 100%;

      @include tablet-large {
        margin-top: $space-24;
      }

      img {
        display: block;
        border-radius: $border-radius-xx-small;
      }
    }
  }

  &.large {
    margin: auto;
    margin-top: 32px;
    width: auto;

    @include tablet-large {
      margin: $space-24 (-$teaser-space);
      display: flex;
      align-items: center;
    }

    .image {
      text-align: right;
      margin-bottom: $space-16;

      @include tablet-large {
        margin-bottom: 0;
      }
    }

    &:nth-child(odd) {
      flex-direction: row-reverse;

      .image {
        text-align: left;
      }
    }

    .block {
      @include tablet-large {
        width: 50%;
        padding: 0 $teaser-space;
      }
    }

    .header {
      display: flex;
      align-items: center;

      .icon {
        margin-left: $space-16;
      }

      h3 {
        @include headline-bold;
        color: $interaction-color-primary;
        margin: 0;
      }
    }

    h2 {
      @include primary-title-medium;
      margin: $space-16 0;

      @include tablet-large {
        margin: $space-24 0;
      }
    }
  }
}
</style>
